import React from 'react';

import { DatePickerInput } from '@mantine/dates';
import { IconCalendar } from '@tabler/icons-react';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/es';
import 'dayjs/locale/it';
import { useTranslation } from 'react-i18next';

import '../../styles/GlassDatePicker.css';

type GlassDatePickerProps = {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  minDate?: Date;
  maxDate?: Date;
  className?: string;
  ariaLabel?: string;
};

const DATE_FORMAT = 'YYYY-MM-DD';

export const GlassDatePicker: React.FC<GlassDatePickerProps> = ({
  value,
  onChange,
  label,
  placeholder,
  minDate,
  maxDate,
  className = '',
  ariaLabel,
}) => {
  const { i18n, t } = useTranslation('common');

  const normalizedLocale = (i18n.language ?? 'en').split('-')[0];
  const parsedValue = value ? dayjs(value, DATE_FORMAT).toDate() : null;
  const clearLabel = t('common.clearSelection', 'Clear selection');

  return (
    <DatePickerInput
      value={parsedValue}
      onChange={(date) => onChange(date ? dayjs(date).format(DATE_FORMAT) : '')}
      valueFormat="DD MMM YYYY"
      label={label}
      placeholder={placeholder ?? label}
      locale={normalizedLocale}
      minDate={minDate}
      maxDate={maxDate}
      firstDayOfWeek={1}
      size="md"
      radius="md"
      className={`glass-date-picker ${className}`.trim()}
      aria-label={ariaLabel ?? label}
      leftSection={<IconCalendar size={18} stroke={1.6} />}
      leftSectionPointerEvents="none"
      clearable
      clearButtonProps={{ 'aria-label': clearLabel }}
      popoverProps={{
        classNames: { dropdown: 'glass-date-picker__dropdown' },
        shadow: 'xl',
        offset: 2,
        withinPortal: false,
        zIndex: 13_000,
      }}
      classNames={{
        input: 'glass-date-picker__input',
        label: 'glass-date-picker__label',
        wrapper: 'glass-date-picker__wrapper',
        day: 'glass-date-picker__day',
        weekday: 'glass-date-picker__weekday',
        month: 'glass-date-picker__month',
        calendarHeader: 'glass-date-picker__calendar-header',
        calendarHeaderControl: 'glass-date-picker__calendar-nav',
        calendarHeaderLevel: 'glass-date-picker__calendar-title',
        calendarHeaderControlIcon: 'glass-date-picker__calendar-nav-icon',
      }}
    />
  );
};
